import { config } from "../../config/config";
import {
  subscriptionDeleteConflictSchema,
  subscriptionHistoryResponseSchema,
  subscriptionSchema,
  subscriptionUpdateConflictSchema,
  type Subscription,
  type SubscriptionDeleteConflict,
  type SubscriptionHistoryResponse,
  type SubscriptionRow,
  type SubscriptionUpdateConflict,
  unfreezeSubscriptionResponseSchema,
  type FreezeSubscriptionPayload,
  type UpdateSubscriptionPayload,
} from "../../utils/zod/subscription";
import { authHeaders, handleError } from "./_helpers";

export type FilterStatus =
  | "ACTIVE"
  | "EXPIRED"
  | "PENDING"
  | "FROZEN"
  | "ALL";
export type SortOption = "DAYS_ASC" | "DAYS_DESC";

export interface SubscriptionsMeta {
  totalCount: number;
  totalPages: number;
  currentPage: number;
  hasNextPage: boolean;
}

/** Backend returns SubscriptionDTO[] for MANAGER/SUPERADMIN or SubscriptionStaffDTO[] for STAFF. */
export interface SubscriptionsResponse {
  data: SubscriptionRow[];
  meta: SubscriptionsMeta;
}

export interface GetSubscriptionsParams {
  search?: string;
  filterStatus?: FilterStatus;
  sort?: SortOption;
  page?: number;
  pageSize?: number;
  expiringInDays?: number;
  expiredSinceDays?: number;
  filterPlanPublicId?: string;
}

export const getSubscriptionsRequest = async (
  token: string,
  params: GetSubscriptionsParams = {},
): Promise<SubscriptionsResponse> => {
  const searchParams = new URLSearchParams();
  if (params.search !== undefined && params.search !== "")
    searchParams.set("search", params.search);
  if (params.filterStatus !== undefined && params.filterStatus !== "ALL")
    searchParams.set("filterStatus", params.filterStatus);
  if (params.sort !== undefined) searchParams.set("sort", params.sort);
  if (params.page !== undefined) searchParams.set("page", String(params.page));
  if (params.pageSize !== undefined)
    searchParams.set("pageSize", String(params.pageSize));
  if (params.expiringInDays !== undefined)
    searchParams.set("expiringInDays", String(params.expiringInDays));
  if (params.expiredSinceDays !== undefined)
    searchParams.set("expiredSinceDays", String(params.expiredSinceDays));
  if (
    params.filterPlanPublicId !== undefined &&
    params.filterPlanPublicId.trim() !== ""
  )
    searchParams.set("plan", params.filterPlanPublicId);

  const query = searchParams.toString();
  const url = `${config.API_URL}/subscriptions${query ? `?${query}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error("No tienes permiso para obtener esta información");
    }
    throw new Error(
      response.statusText || "Error al obtener las suscripciones",
    );
  }
  return response.json();
};

export const getSubscriptionRequest = async (
  publicId: string,
  token: string,
): Promise<Subscription> => {
  const response = await fetch(
    `${config.API_URL}/subscriptions/${encodeURIComponent(publicId)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Suscripción no encontrada");
    }
    if (response.status === 403) {
      throw new Error("No tienes permiso para obtener esta información");
    }
    throw new Error(response.statusText || "Error al obtener la suscripción");
  }

  return subscriptionSchema.parse(await response.json());
};

export const updateSubscriptionRequest = async (
  publicId: string,
  payload: UpdateSubscriptionPayload,
  token: string,
): Promise<Subscription> => {
  const response = await fetch(
    `${config.API_URL}/subscriptions/${encodeURIComponent(publicId)}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    if (response.status === 400) {
      const data = await response.json().catch(() => null);
      const message =
        typeof data?.error === "string"
          ? data.error
          : typeof data?.message === "string"
            ? data.message
            : "Datos inválidos";
      throw new Error(message);
    }
    if (response.status === 403) {
      throw new Error("No tienes permiso para editar esta suscripción");
    }
    if (response.status === 404) {
      throw new Error("Suscripción no encontrada");
    }
    if (response.status === 409) {
      const data = await response.json().catch(() => null);
      const parsed = subscriptionUpdateConflictSchema.safeParse(data);
      if (parsed.success) {
        throw new SubscriptionUpdateConflictError(
          parsed.data,
          typeof data?.error === "string"
            ? data.error
            : "La suscripción tiene una renovación automática activa",
        );
      }
      const message =
        typeof data?.error === "string"
          ? data.error
          : typeof data?.message === "string"
            ? data.message
            : "Conflicto al actualizar la suscripción";
      throw new Error(message);
    }
    throw new Error(
      response.statusText || "Error al actualizar la suscripción",
    );
  }

  return subscriptionSchema.parse(await response.json());
};

/**
 * Congelar y reanudar viven bajo el prefijo `/v1` (router nuevo del backend).
 * Devuelven un resumen de la operación, no la suscripción completa: el detalle
 * se refresca invalidando la query.
 */
export const freezeSubscriptionRequest = async (
  publicId: string,
  payload: FreezeSubscriptionPayload,
  token: string,
): Promise<void> => {
  const response = await fetch(
    `${config.API_URL}/v1/subscriptions/${encodeURIComponent(publicId)}/freeze`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    await handleError(response, "Error al congelar la suscripción");
  }
};

export const unfreezeSubscriptionRequest = async (
  publicId: string,
  token: string,
): Promise<{ frozenDays: number; endDate: string }> => {
  const response = await fetch(
    `${config.API_URL}/v1/subscriptions/${encodeURIComponent(publicId)}/unfreeze`,
    {
      method: "POST",
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    await handleError(response, "Error al reanudar la suscripción");
  }

  return unfreezeSubscriptionResponseSchema.parse(await response.json());
};

/** Typed error thrown by updateSubscriptionRequest when el backend devuelve un 409 estructurado (auto-renewal). */
export class SubscriptionUpdateConflictError extends Error {
  readonly conflict: SubscriptionUpdateConflict;
  constructor(conflict: SubscriptionUpdateConflict, message: string) {
    super(message);
    this.name = "SubscriptionUpdateConflictError";
    this.conflict = conflict;
  }
}

/** Typed error thrown by deleteSubscriptionRequest when the backend returns a structured 409. */
export class SubscriptionDeleteConflictError extends Error {
  readonly conflict: SubscriptionDeleteConflict;
  constructor(conflict: SubscriptionDeleteConflict, message: string) {
    super(message);
    this.name = "SubscriptionDeleteConflictError";
    this.conflict = conflict;
  }
}

export const deleteSubscriptionRequest = async (
  publicId: string,
  alsoDeletePayment: boolean,
  token: string,
): Promise<void> => {
  const url = new URL(
    `${config.API_URL}/subscriptions/${encodeURIComponent(publicId)}`,
  );
  if (alsoDeletePayment) url.searchParams.set("alsoDeletePayment", "true");

  const response = await fetch(url.toString(), {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.ok) return;

  if (response.status === 403) {
    throw new Error("No tienes permiso para eliminar esta suscripción");
  }
  if (response.status === 404) {
    throw new Error("Suscripción no encontrada");
  }
  if (response.status === 409) {
    const data = await response.json().catch(() => null);
    const parsed = subscriptionDeleteConflictSchema.safeParse(data);
    if (parsed.success) {
      const message =
        parsed.data.code === "BOOKINGS_EXIST"
          ? "La suscripción tiene reservas vinculadas"
          : "La suscripción tiene auto-renovación activa";
      throw new SubscriptionDeleteConflictError(parsed.data, message);
    }
    const fallback =
      typeof data?.error === "string"
        ? data.error
        : typeof data?.message === "string"
          ? data.message
          : "Conflicto al eliminar la suscripción";
    throw new Error(fallback);
  }
  throw new Error(response.statusText || "Error al eliminar la suscripción");
};

export interface GetSubscriptionHistoryParams {
  page?: number;
  pageSize?: number;
}

export const getSubscriptionHistoryRequest = async (
  publicId: string,
  token: string,
  params: GetSubscriptionHistoryParams = {},
): Promise<SubscriptionHistoryResponse> => {
  const url = new URL(
    `${config.API_URL}/subscriptions/${encodeURIComponent(publicId)}/history`,
  );
  if (params.page !== undefined) url.searchParams.set("page", String(params.page));
  if (params.pageSize !== undefined)
    url.searchParams.set("pageSize", String(params.pageSize));

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error("No tienes permiso para ver el historial");
    }
    if (response.status === 404) {
      throw new Error("Suscripción no encontrada");
    }
    throw new Error(response.statusText || "Error al obtener el historial");
  }

  return subscriptionHistoryResponseSchema.parse(await response.json());
};

export type ReminderPayload = {
  to: string;
  userName: string;
  planName: string;
  expiryDate: string;
  clientId?: number;
};

export type LastEmail = {
  id: number;
  clientId?: number;
  publicId?: string;
  clientName: string;
  recipient: string;
  subject: string;
  mail_type: string;
  status: string;
  sentAt: string | null;
  errorMessage: string | null;
  sentBy: string;
};

export const getLastEmailsByTenantRequest = async (token: string) => {
  if (!config.MAIL_API_URL) {
    return { count: 0, data: [] as LastEmail[] };
  }
  const r = await fetch(`${config.MAIL_API_URL}/mail/last-emails-by-tenant`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!r.ok) {
    throw new Error(r.statusText || "Error al obtener últimos correos");
  }
  const data = await r.json();
  return data as { count: number; data: LastEmail[] };
};
